import { useEffect, useState } from 'react';
import { fromAddress } from 'react-geocode';
import {
  FripLocationParam,
  ProductParamInput,
} from 'containers/ProductRegist/hooks/types';
import { useMutationUpdateLocation } from '../graphQL/hooks/useMutationUpdateLocation';
import { CreateLocationResult, LocationParam } from '../graphQL/types';
import { errorHandler } from 'utils/ErrorHandler';

export interface AddressItems {
  locationId?: string;
  overseas: boolean;
  address: string;
  detailAddress: string;
  label: string;
  locationType: string[];
  convenienceIds: string[];
  geoPoint: { latitude: number; longitude: number };
  parking: {
    available: boolean;
    note: string;
  };
}

const createInitialAddressItems = (): AddressItems => ({
  overseas: false,
  address: '',
  detailAddress: '',
  label: '',
  locationType: [],
  geoPoint: {
    latitude: 0,
    longitude: 0,
  },
  parking: {
    available: false,
    note: '',
  },
  convenienceIds: [],
});

/**
 * 상품 장소 항목을 모달 입력 상태로 변환한다.
 * 주차·편의시설은 항목에 실린 장소 템플릿 값을 그대로 쓰고, 없으면(템플릿이 없는 옛 주소) 기본값을 쓴다.
 */
const toAddressItems = (item: FripLocationParam): AddressItems => {
  const initial = createInitialAddressItems();

  return {
    ...initial,
    locationId: item.locationId,
    address: item.address,
    label: item.label,
    overseas: item.overseas,
    geoPoint: item.geoPoint ?? initial.geoPoint,
    parking: {
      available: item.parking?.available ?? false,
      note: item.parking?.note ?? '',
    },
    convenienceIds: item.convenienceIds ?? [],
  };
};

export const useAddressModalHooks = (
  productParamInput: ProductParamInput,
  locationVenueItems: FripLocationParam | null,
  type: 'address' | 'meeting',
  handleOnChangeProductParamInput: (key: string, value: any) => void,
  handleResetLocationVenueItems: () => void,
) => {
  const { handleCreateLocation, handleUpdateLocation, handleDeleteLocation } =
    useMutationUpdateLocation();

  const [isOpenKakaoModal, setIsOpenKakaoModal] = useState<boolean>(false);
  const [isMapLoad, setIsMapLoad] = useState<boolean>(false);

  const [addressItems, setAddressItems] = useState<AddressItems>(
    createInitialAddressItems,
  );

  const handleSetAddressItems = (key: string, value: any) => {
    setAddressItems({ ...addressItems, [key]: value });
  };

  const handleIsOpenKakaoModal = () => {
    setIsOpenKakaoModal(!isOpenKakaoModal);
  };

  const handleFindAddress = async () => {
    try {
      const response = await fromAddress(addressItems.address);

      if (response) {
        const { lat, lng } = response.results[0].geometry.location;

        setIsMapLoad(true);
        setAddressItems({
          ...addressItems,
          geoPoint: {
            latitude: lat,
            longitude: lng,
          },
        });
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleIsActiveButton = (): boolean => {
    if (addressItems.overseas && addressItems.address !== '') {
      return true;
    }

    if (addressItems.address !== '' && addressItems.label !== '') {
      return true;
    }

    return false;
  };

  const handleRegistLocation = (callback?: () => void) => {
    if (!handleIsActiveButton()) {
      return;
    }

    const address =
      addressItems.detailAddress && addressItems.detailAddress !== ''
        ? `${addressItems.address} ${addressItems.detailAddress}`
        : addressItems.address;

    // 장소 템플릿(Location)에 저장할 값. 주차·편의시설은 상품이 아니라 템플릿에만 저장된다.
    const locationParam: LocationParam = {
      name: addressItems.label,
      address,
      geoPoint: addressItems.geoPoint,
      parking: addressItems.parking,
      convenienceIds: addressItems.convenienceIds,
      overseas: addressItems.overseas,
      useByDefault: false,
    };

    const buildLocationItem = (
      id: string,
      locationId: string,
    ): FripLocationParam => ({
      id,
      locationId,
      label: addressItems.label,
      address,
      geoPoint: addressItems.geoPoint,
      overseas: addressItems.overseas,
      parking: addressItems.parking,
      convenienceIds: addressItems.convenienceIds,
    });

    const applyLocationItem = (item: FripLocationParam) => {
      if (type === 'meeting') {
        handleOnChangeProductParamInput('frip', {
          ...productParamInput.frip,
          locationOfGathering: item,
        });

        return;
      }

      const locationsOfVenue = [...productParamInput.frip!.locationsOfVenue];

      if (locationVenueItems === null) {
        locationsOfVenue.push(item);
      } else {
        const index = locationsOfVenue.findIndex(
          venueItem => venueItem.id === locationVenueItems.id,
        );

        if (index === -1) {
          return;
        }

        locationsOfVenue[index] = item;
      }

      handleOnChangeProductParamInput('frip', {
        ...productParamInput.frip,
        locationsOfVenue,
      });
    };

    if (locationVenueItems === null) {
      // 새 주소: 템플릿을 만들고 그 ID로 상품 장소 항목을 추가한다.
      handleCreateLocation(locationParam, (result: CreateLocationResult) => {
        applyLocationItem(
          buildLocationItem(result.createLocation.id, result.createLocation.id),
        );
      });

      callback && callback();

      return;
    }

    if (locationVenueItems.locationId) {
      // 기존 주소: 연결된 템플릿을 갱신해 주차·편의시설 변경을 서버에 반영한다.
      handleUpdateLocation(
        locationVenueItems.locationId,
        locationParam,
        () => {},
      );
      applyLocationItem(
        buildLocationItem(locationVenueItems.id, locationVenueItems.locationId),
      );
    } else {
      // 템플릿이 없는 옛 주소: 템플릿을 새로 만들어 연결한다.
      handleCreateLocation(locationParam, (result: CreateLocationResult) => {
        applyLocationItem(
          buildLocationItem(locationVenueItems.id, result.createLocation.id),
        );
      });
    }

    handleResetLocationVenueItems();
    callback && callback();
  };

  useEffect(() => {
    setAddressItems(
      locationVenueItems !== null
        ? toAddressItems(locationVenueItems)
        : createInitialAddressItems(),
    );
  }, [locationVenueItems]);

  return {
    isOpenKakaoModal,
    isMapLoad,
    addressItems,
    setAddressItems,
    setIsMapLoad,
    handleIsOpenKakaoModal,
    handleSetAddressItems,
    handleFindAddress,
    handleIsActiveButton,
    handleRegistLocation,
    handleDeleteLocation,
  };
};
